import { FC, useState } from 'react'
import Link from 'next/link'
import { Button } from '@corratech/pylot-ui'
import { ConfirmBox } from '@components/alfakher/pylot-ui'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { Cross, Edit } from '@components/icons'
import { useTranslation } from 'next-i18next'
import { Maybe, WishlistItemInterface, Wishlist } from '@pylot-data/pylotschema'
import { useRemoveProductsFromWishlist } from '@corratech/pylot-wishlist/src/Hooks/use-remove-products-from-wishlist'
import s from '@pagestyles/Wishlist.module.scss'
import cn from 'classnames'
import { Product_Url } from '@components/UrlConfiguration/UrlConfiguration'

export type Props = {
    wishlist: Wishlist
    item: Maybe<WishlistItemInterface>
}

export const DeleteWishlistItem: FC<Props> = ({ item, wishlist }) => {
    const { t } = useTranslation(['wishlist'])
    const [isVisible, setIsVisible] = useState(false)
    const { openToast } = useUI()
    const {
        isLoading,
        removeProductsFromWishlist
    } = useRemoveProductsFromWishlist()
    const productName = item!.product?.name

    const showConfirm = () => setIsVisible(true)
    const closeConfirm = () => setIsVisible(false)

    const handleRemoveProduct = async () => {
        closeConfirm()
        try {
            const response = await removeProductsFromWishlist({
                wishlistId: String(wishlist!.id),
                wishlistItemsIds: [String(item!.id)]
            })

            if (response?.errors?.length) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }

            if (response?.data?.removeProductsFromWishlist?.wishlist) {
                openToast(
                    t(
                        'wishlist|"{{ name }}" has been removed from the wish list',
                        { name: productName }
                    ),
                    ToastType.Success
                )
            }
        } catch (e) {
            if (e) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        }
    }

    return (
        <div className={s['delete-container']}>
            <span className={cn('edit-icon', s['edit-icon'])}>
                <Link href={`${Product_Url}/${item?.product?.url_key}/`}>
                    <Edit />
                </Link>
            </span>
            <Button
                type="button"
                variant="link"
                loading={isLoading}
                disabled={isLoading}
                onClick={showConfirm}
            >
                <Cross />
            </Button>

            <ConfirmBox
                show={isVisible}
                closeConfirm={closeConfirm}
                handleAction={handleRemoveProduct}
            >
                {t(
                    `wishlist|Are you sure you want to delete item "{{ name }}"? This action can't be undone.`,
                    { name: productName }
                )}
            </ConfirmBox>
        </div>
    )
}
